import { FormBuilder, FormGroup } from '@angular/forms';

import { VendaItem } from '../../../../../shared/services/models/vendas/venda-item.model';
import { VendaItemImposto } from '../../../../../shared/services/models/vendas/venda-item-imposto.model';
import { montarFormItemVenda } from './venda-item.form';

/**
 * A tradução entre o item do servidor e o item ANINHADO do formulário.
 *
 * As colunas de `venda_item` trafegam planas e o formulário as agrupa em `dados`, `kit` e
 * `comissao`: para elas vale o MAPA, um só, usado nas duas direções (escrever ida e volta à mão
 * garantiria que uma delas ficasse para trás). Os tributos trafegam ANINHADOS em `item.imposto`
 * (`venda_item_imposto`, ex.: `imposto.codigoCstIcms`), e o form os agrupa em `icms`, `ipi`, `pis`,
 * `cofins`, `ibsCbs` e `difal` com os nomes do item da NF-e de saída (ex.: `icms.codigoCst`), que é o
 * que as abas de tributo do modal usam: cada coluna tem a sua entrada no MAPA_IMPOSTO. Item sem
 * imposto (`imposto` nulo) abre com os controles de tributo vazios. Lotes e séries (`lotes`, `series`)
 * são valores simples do form, com os nomes do item da NF-e de saída.
 *
 * Campos do servidor que NÃO estão no formulário ficam de fora de propósito: `idEmpresa`,
 * `idVenda` e `datetimeInclusao` são do servidor, e `idVendaItemImposto` do imposto também; `descricaoProduto`, `isKit`,
 * `isControlaMultiplo`, `isUnidadeMedidaFracionada`, `idUnidadeMultiplo`, `fatorMultiplo` e
 * `unidadeMultiplo` são do CADASTRO do produto, e o item guarda o snapshot dele, não a referência viva.
 * `isControlaLotes` e `isControlaSeries` (form `dados`) também são só da tela.
 */

/** `campo do servidor` → `caminho no formulário`. Só os que existem dos dois lados. */
const MAPA: Record<string, string> = {
    // ---- dados ------------------------------------------------------------------
    idVendaItem: 'dados.idVendaItem',
    sequencia: 'dados.sequencia',
    idProduto: 'dados.idProduto',
    descricao: 'dados.descricao',
    descricaoComplementar: 'dados.descricaoComplementar',
    codigoSku: 'dados.codigoSku',
    gtinEan: 'dados.gtinEan',
    quantidade: 'dados.quantidade',
    idUnidadeMedida: 'dados.idUnidadeMedida',
    siglaUnidade: 'dados.siglaUnidade',
    precoBrutoUnitario: 'dados.precoBrutoUnitario',
    descontoPercentual: 'dados.descontoPercentual',
    precoLiquidoUnitario: 'dados.precoLiquidoUnitario',
    valorTotal: 'dados.valorTotal',
    descontoUnitario: 'dados.descontoUnitario',
    valorDescontoRateado: 'dados.valorDescontoRateado',
    diferencaPrecoBrutoLiquidoPercentual: 'dados.diferencaPrecoBrutoLiquidoPercentual',
    prazoDisponibilidadeDias: 'dados.prazoDisponibilidadeDias',
    idListaPreco: 'dados.idListaPreco',
    isListaPrecoManual: 'dados.isListaPrecoManual',
    idLocalizacaoEstoque: 'dados.idLocalizacaoEstoque',
    pesoBruto: 'dados.pesoBruto',
    pesoLiquido: 'dados.pesoLiquido',
    idTipoItemSped: 'dados.idTipoItemSped',
    numeroPedidoCliente: 'dados.numeroPedidoCliente',
    numeroItemPedido: 'dados.numeroItemPedido',
    idNatOp: 'dados.idNatOp',
    codigoCfop: 'dados.codigoCfop',
    codigoNcm: 'dados.codigoNcm',
    codigoBeneficioFiscal: 'dados.codigoBeneficioFiscal',
    tributosInformadosManualmente: 'dados.tributosInformadosManualmente',

    // ---- kit --------------------------------------------------------------------
    idProdutoKit: 'kit.idProdutoKit',
    numeroKit: 'kit.numeroKit',
    quantidadeKit: 'kit.quantidadeKit',
    precoKit: 'kit.precoKit',
    percentualDescontoKit: 'kit.percentualDescontoKit',

    // ---- comissão ---------------------------------------------------------------
    percentualComissao: 'comissao.percentualComissao',
    valorComissao: 'comissao.valorComissao',
};

/**
 * `campo do servidor em imposto` → `caminho no formulário`: os tributos, que viajam ANINHADOS em `item.imposto`
 * (venda_item_imposto, 1:1). Mesmo mapeamento explícito campo a campo, nas duas direções.
 */
const MAPA_IMPOSTO: Record<string, string> = {
    // ---- tributo: ICMS (grupo `icms`) ----------------------------------------------
    codigoOrigemMercadoriaIcms: 'icms.codigoOrigemMercadoria',
    codigoCstIcms: 'icms.codigoCst',
    codigoCsosnIcms: 'icms.codigoCsosn',
    codigoModalidadeBaseCalculoIcms: 'icms.codigoModalidadeBaseCalculo',
    valorBaseCalculoIcms: 'icms.valorBaseCalculo',
    percentualReducaoBaseCalculoIcms: 'icms.percentualReducaoBaseCalculo',
    percentualAliquotaIcms: 'icms.percentualAliquota',
    valorIcmsOperacao: 'icms.valorIcmsOperacao',
    valorIcms: 'icms.valorIcms',
    valorIcmsDesonerado: 'icms.valorIcmsDesonerado',
    codigoMotivoDesoneracaoIcms: 'icms.codigoMotivoDesoneracao',
    codigoIndicadorDeducaoDesoneracaoIcms: 'icms.codigoIndicadorDeducaoDesoneracao',
    valorBaseFcp: 'icms.valorBaseFcp',
    percentualFcp: 'icms.percentualFcp',
    valorFcp: 'icms.valorFcp',
    codigoModalidadeBaseCalculoIcmsSt: 'icms.codigoModalidadeBaseCalculoSt',
    percentualMvaIcmsSt: 'icms.percentualMvaSt',
    percentualReducaoBaseCalculoIcmsSt: 'icms.percentualReducaoBaseCalculoSt',
    valorBaseCalculoIcmsSt: 'icms.valorBaseCalculoSt',
    percentualAliquotaIcmsSt: 'icms.percentualAliquotaSt',
    valorIcmsSt: 'icms.valorIcmsSt',
    valorBaseFcpSt: 'icms.valorBaseFcpSt',
    percentualFcpSt: 'icms.percentualFcpSt',
    valorFcpSt: 'icms.valorFcpSt',
    valorBaseCalculoIcmsStRetido: 'icms.valorBaseCalculoStRetido',
    percentualSuporteIcmsStRetido: 'icms.percentualSuporteStRetido',
    valorIcmsStRetido: 'icms.valorIcmsStRetido',
    valorBaseFcpStRetido: 'icms.valorBaseFcpStRetido',
    percentualFcpStRetido: 'icms.percentualFcpStRetido',
    valorFcpStRetido: 'icms.valorFcpStRetido',
    percentualDiferimentoIcms: 'icms.percentualDiferimento',
    valorIcmsDiferido: 'icms.valorIcmsDiferido',
    valorBaseCalculoEfetivoIcms: 'icms.valorBaseCalculoEfetivo',
    percentualAliquotaEfetivaIcms: 'icms.percentualAliquotaEfetiva',
    valorIcmsEfetivo: 'icms.valorIcmsEfetivo',
    percentualCreditoIcms: 'icms.percentualCredito',
    valorCreditoIcms: 'icms.valorCredito',
    codigoCreditoPresumidoIcms: 'icms.codigoCreditoPresumidoIcms',
    percentualCreditoPresumidoIcms: 'icms.percentualCreditoPresumidoIcms',
    valorCreditoPresumidoIcms: 'icms.valorCreditoPresumidoIcms',
    quantidadeBaseMonofasicaIcms: 'icms.quantidadeBaseMonofasica',
    valorAliquotaAdremMonofasicaIcms: 'icms.valorAliquotaAdremMonofasica',
    valorIcmsMonofasico: 'icms.valorIcmsMonofasico',
    quantidadeBaseMonofasicaRetencaoIcms: 'icms.quantidadeBaseMonofasicaRetencao',
    valorAliquotaAdremMonofasicaRetencaoIcms: 'icms.valorAliquotaAdremMonofasicaRetencao',
    valorIcmsMonofasicoRetencao: 'icms.valorIcmsMonofasicoRetencao',
    quantidadeBaseMonofasicaRetidoIcms: 'icms.quantidadeBaseMonofasicaRetido',
    valorAliquotaAdremMonofasicaRetidoIcms: 'icms.valorAliquotaAdremMonofasicaRetido',
    valorIcmsMonofasicoRetido: 'icms.valorIcmsMonofasicoRetido',
    percentualReducaoAdremIcms: 'icms.percentualReducaoAdrem',

    // ---- tributo: IPI (grupo `ipi`) ------------------------------------------------
    codigoCstIpi: 'ipi.codigoCst',
    codigoEnquadramentoIpi: 'ipi.codigoEnquadramento',
    cnpjProdutorIpi: 'ipi.cnpjProdutor',
    codigoSeloIpi: 'ipi.codigoSelo',
    quantidadeSeloIpi: 'ipi.quantidadeSelo',
    valorBaseCalculoIpi: 'ipi.valorBaseCalculo',
    percentualAliquotaIpi: 'ipi.percentualAliquota',
    quantidadeUnidadeIpi: 'ipi.quantidadeUnidade',
    valorUnidadeIpi: 'ipi.valorUnidade',
    valorIpi: 'ipi.valorIpi',

    // ---- tributo: PIS (grupo `pis`) ------------------------------------------------
    codigoCstPis: 'pis.codigoCst',
    valorBaseCalculoPis: 'pis.valorBaseCalculo',
    percentualAliquotaPis: 'pis.percentualAliquota',
    quantidadeBaseCalculoPis: 'pis.quantidadeBaseCalculo',
    valorAliquotaUnidadePis: 'pis.valorAliquotaUnidade',
    valorPis: 'pis.valorPis',
    valorBaseCalculoPisSt: 'pis.valorBaseCalculoSt',
    percentualAliquotaPisSt: 'pis.percentualAliquotaSt',
    quantidadeBaseCalculoPisSt: 'pis.quantidadeBaseCalculoSt',
    valorAliquotaUnidadePisSt: 'pis.valorAliquotaUnidadeSt',
    valorPisSt: 'pis.valorPisSt',
    codigoIndicadorComposicaoPisSt: 'pis.codigoIndicadorComposicaoSt',

    // ---- tributo: COFINS (grupo `cofins`) ------------------------------------------
    codigoCstCofins: 'cofins.codigoCst',
    valorBaseCalculoCofins: 'cofins.valorBaseCalculo',
    percentualAliquotaCofins: 'cofins.percentualAliquota',
    quantidadeBaseCalculoCofins: 'cofins.quantidadeBaseCalculo',
    valorAliquotaUnidadeCofins: 'cofins.valorAliquotaUnidade',
    valorCofins: 'cofins.valorCofins',
    valorBaseCalculoCofinsSt: 'cofins.valorBaseCalculoSt',
    percentualAliquotaCofinsSt: 'cofins.percentualAliquotaSt',
    quantidadeBaseCalculoCofinsSt: 'cofins.quantidadeBaseCalculoSt',
    valorAliquotaUnidadeCofinsSt: 'cofins.valorAliquotaUnidadeSt',
    valorCofinsSt: 'cofins.valorCofinsSt',
    codigoIndicadorComposicaoCofinsSt: 'cofins.codigoIndicadorComposicaoSt',

    // ---- tributo: IBS e CBS (grupo `ibsCbs`) ---------------------------------------
    codigoCstIbsCbs: 'ibsCbs.codigoCst',
    codigoClassificacaoTributariaIbsCbs: 'ibsCbs.codigoClassificacaoTributaria',
    indicadorDoacaoIbsCbs: 'ibsCbs.indicadorDoacao',
    valorBaseCalculoIbsCbs: 'ibsCbs.valorBaseCalculo',
    percentualAliquotaIbsUf: 'ibsCbs.percentualAliquotaIbsUf',
    valorIbsUf: 'ibsCbs.valorIbsUf',
    percentualAliquotaIbsMunicipal: 'ibsCbs.percentualAliquotaIbsMunicipal',
    valorIbsMunicipal: 'ibsCbs.valorIbsMunicipal',
    percentualAliquotaCbs: 'ibsCbs.percentualAliquotaCbs',
    valorCbs: 'ibsCbs.valorCbs',
    valorIbs: 'ibsCbs.valorIbs',
    percentualDiferimentoIbsUf: 'ibsCbs.percentualDiferimentoIbsUf',
    valorDiferimentoIbsUf: 'ibsCbs.valorDiferimentoIbsUf',
    percentualDiferimentoIbsMunicipal: 'ibsCbs.percentualDiferimentoIbsMunicipal',
    valorDiferimentoIbsMunicipal: 'ibsCbs.valorDiferimentoIbsMunicipal',
    percentualDiferimentoCbs: 'ibsCbs.percentualDiferimentoCbs',
    valorDiferimentoCbs: 'ibsCbs.valorDiferimentoCbs',
    valorDevolucaoIbsUf: 'ibsCbs.valorDevolucaoIbsUf',
    valorDevolucaoIbsMunicipal: 'ibsCbs.valorDevolucaoIbsMunicipal',
    valorDevolucaoCbs: 'ibsCbs.valorDevolucaoCbs',
    percentualReducaoIbsUf: 'ibsCbs.percentualReducaoIbsUf',
    percentualAliquotaEfetivaIbsUf: 'ibsCbs.percentualAliquotaEfetivaIbsUf',
    percentualReducaoIbsMunicipal: 'ibsCbs.percentualReducaoIbsMunicipal',
    percentualAliquotaEfetivaIbsMunicipal: 'ibsCbs.percentualAliquotaEfetivaIbsMunicipal',
    percentualReducaoCbs: 'ibsCbs.percentualReducaoCbs',
    percentualAliquotaEfetivaCbs: 'ibsCbs.percentualAliquotaEfetivaCbs',
    codigoTipoAlcZfmCbs: 'ibsCbs.codigoTipoAlcZfmCbs',
    numeroProcessoSuframaIbsCbs: 'ibsCbs.numeroProcessoSuframa',
    percentualAliquotaEfetivaRegularCbsAlcZfm: 'ibsCbs.percentualAliquotaEfetivaRegularCbsAlcZfm',
    valorTributoRegularCbsAlcZfm: 'ibsCbs.valorTributoRegularCbsAlcZfm',
    percentualAliquotaEfetivaRegularIbsUf: 'ibsCbs.percentualAliquotaEfetivaRegularIbsUf',
    valorRegularIbsUf: 'ibsCbs.valorRegularIbsUf',
    percentualAliquotaEfetivaRegularIbsMunicipal: 'ibsCbs.percentualAliquotaEfetivaRegularIbsMunicipal',
    valorRegularIbsMunicipal: 'ibsCbs.valorRegularIbsMunicipal',
    percentualAliquotaEfetivaRegularCbs: 'ibsCbs.percentualAliquotaEfetivaRegularCbs',
    valorRegularCbs: 'ibsCbs.valorRegularCbs',
    percentualAliquotaCompraGovernoIbsUf: 'ibsCbs.percentualAliquotaCompraGovernoIbsUf',
    valorCompraGovernoIbsUf: 'ibsCbs.valorCompraGovernoIbsUf',
    percentualAliquotaCompraGovernoIbsMunicipal: 'ibsCbs.percentualAliquotaCompraGovernoIbsMunicipal',
    valorCompraGovernoIbsMunicipal: 'ibsCbs.valorCompraGovernoIbsMunicipal',
    percentualAliquotaCompraGovernoCbs: 'ibsCbs.percentualAliquotaCompraGovernoCbs',
    valorCompraGovernoCbs: 'ibsCbs.valorCompraGovernoCbs',
    quantidadeBaseMonofasicaPadraoIbsCbs: 'ibsCbs.quantidadeBaseMonofasicaPadrao',
    valorAliquotaIbsUnidadePadrao: 'ibsCbs.valorAliquotaIbsUnidadePadrao',
    valorIbsMonofasicoPadrao: 'ibsCbs.valorIbsMonofasicoPadrao',
    valorAliquotaCbsUnidadePadrao: 'ibsCbs.valorAliquotaCbsUnidadePadrao',
    valorCbsMonofasicoPadrao: 'ibsCbs.valorCbsMonofasicoPadrao',
    quantidadeBaseMonofasicaRetencaoIbsCbs: 'ibsCbs.quantidadeBaseMonofasicaRetencao',
    valorAliquotaIbsUnidadeRetencao: 'ibsCbs.valorAliquotaIbsUnidadeRetencao',
    valorIbsMonofasicoRetencao: 'ibsCbs.valorIbsMonofasicoRetencao',
    valorAliquotaCbsUnidadeRetencao: 'ibsCbs.valorAliquotaCbsUnidadeRetencao',
    valorCbsMonofasicoRetencao: 'ibsCbs.valorCbsMonofasicoRetencao',
    quantidadeBaseMonofasicaRetidoIbsCbs: 'ibsCbs.quantidadeBaseMonofasicaRetido',
    valorAliquotaIbsUnidadeRetido: 'ibsCbs.valorAliquotaIbsUnidadeRetido',
    valorIbsMonofasicoRetido: 'ibsCbs.valorIbsMonofasicoRetido',
    valorAliquotaCbsUnidadeRetido: 'ibsCbs.valorAliquotaCbsUnidadeRetido',
    valorCbsMonofasicoRetido: 'ibsCbs.valorCbsMonofasicoRetido',
    percentualDiferimentoIbsMonofasico: 'ibsCbs.percentualDiferimentoIbsMonofasico',
    valorIbsMonofasicoDiferido: 'ibsCbs.valorIbsMonofasicoDiferido',
    percentualDiferimentoCbsMonofasico: 'ibsCbs.percentualDiferimentoCbsMonofasico',
    valorCbsMonofasicoDiferido: 'ibsCbs.valorCbsMonofasicoDiferido',
    valorTransferenciaCreditoIbs: 'ibsCbs.valorTransferenciaCreditoIbs',
    valorTransferenciaCreditoCbs: 'ibsCbs.valorTransferenciaCreditoCbs',
    anoMesAjusteCompetenciaIbsCbs: 'ibsCbs.anoMesAjusteCompetencia',
    valorAjusteIbs: 'ibsCbs.valorAjusteIbs',
    valorAjusteCbs: 'ibsCbs.valorAjusteCbs',
    valorEstornoCreditoIbs: 'ibsCbs.valorEstornoCreditoIbs',
    valorEstornoCreditoCbs: 'ibsCbs.valorEstornoCreditoCbs',
    valorBaseCreditoPresumidoIbsCbs: 'ibsCbs.valorBaseCreditoPresumido',
    codigoCreditoPresumidoIbsCbs: 'ibsCbs.codigoCreditoPresumido',
    percentualCreditoPresumidoIbs: 'ibsCbs.percentualCreditoPresumidoIbs',
    valorCreditoPresumidoIbs: 'ibsCbs.valorCreditoPresumidoIbs',
    valorCreditoPresumidoSuspensivoIbs: 'ibsCbs.valorCreditoPresumidoSuspensivoIbs',
    percentualCreditoPresumidoCbs: 'ibsCbs.percentualCreditoPresumidoCbs',
    valorCreditoPresumidoCbs: 'ibsCbs.valorCreditoPresumidoCbs',
    valorCreditoPresumidoSuspensivoCbs: 'ibsCbs.valorCreditoPresumidoSuspensivoCbs',
    anoMesCreditoPresumidoZfmIbsCbs: 'ibsCbs.anoMesCreditoPresumidoZfm',
    codigoTipoCreditoPresumidoZfmIbsCbs: 'ibsCbs.codigoTipoCreditoPresumidoZfm',
    valorCreditoPresumidoZfmIbsCbs: 'ibsCbs.valorCreditoPresumidoZfm',

    // ---- tributo: ICMS DIFAL (grupo `difal`) ---------------------------------------
    valorBaseCalculoDifal: 'difal.valorBaseCalculo',
    percentualFcpDestinoDifal: 'difal.percentualFcpDestino',
    percentualAliquotaInternaDestinoDifal: 'difal.percentualAliquotaInternaDestino',
    percentualAliquotaInterestadualDifal: 'difal.percentualAliquotaInterestadual',
    percentualPartilhaDifal: 'difal.percentualPartilha',
    valorFcpDestinoDifal: 'difal.valorFcpDestino',
    valorIcmsDestinoDifal: 'difal.valorIcmsDestino',
    valorIcmsRemetenteDifal: 'difal.valorIcmsRemetente',
};

/**
 * Monta o FormGroup de UMA linha a partir do item que o servidor devolveu.
 *
 * Não liga recálculo nem comissão: quem faz isso é o dono do FormArray, e de propósito **sem
 * recalcular na hora**. Item carregado mostra o que está GRAVADO — apurar de novo ali trocaria o
 * snapshot do documento por uma prévia, e no kit de preço fixo chegaria a mudar o `valorTotal`,
 * que por decisão de modelagem não é `quantidade × precoLiquidoUnitario`.
 */
export function montarItemDoServidor(fb: FormBuilder, item: Record<string, unknown>): FormGroup {
    const linha = montarFormItemVenda(fb);

    for (const [campoServidor, caminho] of Object.entries(MAPA)) {
        if (!(campoServidor in item)) continue;

        // `undefined` limparia o controle; `null` é valor legítimo e passa.
        const valor = item[campoServidor];

        if (valor === undefined) continue;

        linha.get(caminho)?.setValue(valor as never, { emitEvent: false });
    }

    // Sem `imposto` (item sem tributo gravado) os controles de tributo ficam como nasceram: vazios.
    const imposto = item['imposto'] as Record<string, unknown> | null | undefined;

    if (imposto) {
        for (const [campoServidor, caminho] of Object.entries(MAPA_IMPOSTO)) {
            const valor = imposto[campoServidor];

            if (valor === undefined) continue;

            linha.get(caminho)?.setValue(valor as never, { emitEvent: false });
        }
    }

    linha.get('lotes')?.setValue((item['lotes'] as unknown[] | null | undefined) ?? [], { emitEvent: false });
    linha.get('series')?.setValue((item['series'] as unknown[] | null | undefined) ?? [], { emitEvent: false });

    completarDescricaoDoCadastro(linha, item);
    completarKitDerivado(linha, item);
    completarMultiploDoServidor(linha, item);
    completarUnidadeFracionadaDoServidor(linha, item);
    completarControleLoteSerieDoServidor(linha, item);

    return linha;
}

/**
 * O controle de lote e série vem do orquestrador no GET (cadastro do produto). Sem ele, a seleção
 * gravada o indica (o salvamento exige lotes de quem controla lote e séries de quem controla série).
 */
function completarControleLoteSerieDoServidor(linha: FormGroup, item: Record<string, unknown>): void {
    linha.get('dados')?.patchValue({
        isControlaLotes: item['isControlaLotes'] === true || ((linha.get('lotes')?.value as unknown[] | null) ?? []).length > 0,
        isControlaSeries: item['isControlaSeries'] === true || ((linha.get('series')?.value as unknown[] | null) ?? []).length > 0,
    }, { emitEvent: false });
}

/**
 * A unidade fracionada vem do orquestrador no GET (cadastro do produto) e decide a máscara da
 * quantidade. Sem ela o item reaberto voltava aceitando decimais numa unidade que não fraciona.
 */
function completarUnidadeFracionadaDoServidor(linha: FormGroup, item: Record<string, unknown>): void {
    linha.get('dados.isUnidadeMedidaFracionada')?.setValue(item['isUnidadeMedidaFracionada'] !== false, { emitEvent: false });
}

/**
 * Item gravado SEM a descrição-snapshot (documentos anteriores à coluna: os seeds do board têm
 * `descricao` nula) mostra o nome do cadastro, que o orquestrador manda em `descricaoProduto`.
 * Sem isso o campo Item abria em branco, como se a linha tivesse perdido o produto — e o
 * próximo salvamento passa a gravar o snapshot que faltava.
 */
function completarDescricaoDoCadastro(linha: FormGroup, item: Record<string, unknown>): void {
    const descricao = linha.get('dados.descricao');
    const doCadastro = item['descricaoProduto'];

    if (descricao && !descricao.value && typeof doCadastro === 'string' && doCadastro) {
        descricao.setValue(doCadastro, { emitEvent: false });
    }
}

/** A regra de múltiplo vem do orquestrador no GET (cadastro do produto) e vive só na tela (§6.2.3). */
function completarMultiploDoServidor(linha: FormGroup, item: Record<string, unknown>): void {
    linha.get('multiplo')?.patchValue({
        isControlaMultiplo: item['isControlaMultiplo'] === true,
        fatorMultiplo: (item['fatorMultiplo'] as number | null | undefined) ?? null,
        unidadeMultiplo: (item['unidadeMultiplo'] as string | null | undefined) ?? null,
    }, { emitEvent: false });
}

function completarKitDerivado(linha: FormGroup, item: Record<string, unknown>): void {
    if (linha.get('kit.numeroKit')?.value == null) return;

    const quantidade = Number(linha.get('dados.quantidade')?.value ?? 0);
    const kits = Number(linha.get('kit.quantidadeKit')?.value ?? 0);

    linha.get('kit')?.patchValue({
        descricaoKit: (item['descricaoProdutoKit'] as string | null | undefined) ?? null,
        codigoSkuKit: (item['codigoSkuProdutoKit'] as string | null | undefined) ?? null,
        isPrecoFixoKit: item['isPrecoFixoKit'] === true,
        quantidadeComposicao: kits > 0 ? quantidade / kits : quantidade,
        precoBaseComponente: Number(linha.get('dados.precoBrutoUnitario')?.value ?? 0),
    }, { emitEvent: false });
}

/**
 * O caminho de volta: a linha do formulário achatada no formato que a tabela espera.
 *
 * O item viaja no request como o MODEL `VendaItem`, e o C# declara `idVendaItem`, `sequencia`,
 * `quantidade`, `precoLiquidoUnitario` e `valorTotal` como não-anuláveis: mandar `null` neles derruba o
 * request inteiro no ModelState (400) antes de qualquer validação de negócio. Item novo não tem
 * id ainda — vai como 0, e o servidor atribui; os numéricos vazios vão como 0, que é o que a
 * validação da folha vai recusar com mensagem própria ("quantidade deve ser maior que zero").
 */
export function achatarItemParaRequest(linha: FormGroup): VendaItem {
    const plano: Record<string, unknown> = {};

    for (const [campoServidor, caminho] of Object.entries(MAPA)) {
        plano[campoServidor] = linha.get(caminho)?.value ?? null;
    }

    for (const campo of NAO_ANULAVEIS_NO_SERVIDOR) {
        plano[campo] = Number(plano[campo] ?? 0) || 0;
    }

    // Também não anuláveis no C#: a lista nasce vazia.
    plano['tributosInformadosManualmente'] = (plano['tributosInformadosManualmente'] as string[] | null) ?? [];
    plano['lotes'] = (linha.get('lotes')?.value as unknown[] | null) ?? [];
    plano['series'] = (linha.get('series')?.value as unknown[] | null) ?? [];

    // Do servidor: reescritos na gravação, mas o model os declara e o C# não os aceita nulos.
    plano['idEmpresa'] = 0;
    plano['idVenda'] = 0;
    plano['datetimeInclusao'] = new Date().toISOString();

    plano['imposto'] = achatarImpostoParaRequest(linha, plano['idVendaItem'] as number);

    // O mapa, o imposto e as listas cobrem o formulário inteiro, mais os 3 campos acima: é o `VendaItem` inteiro.
    return plano as unknown as VendaItem;
}

/** Campos que o model C# declara sem `?` — `null` aqui é 400, não erro de negócio. */
const NAO_ANULAVEIS_NO_SERVIDOR = ['idVendaItem', 'sequencia', 'quantidade', 'precoLiquidoUnitario', 'valorTotal'];

/**
 * Os tributos da linha como o objeto `imposto` do item, ou `null` quando nenhum controle de tributo
 * tem valor (item sem imposto). O id do imposto vai 0: o servidor atribui na gravação, como no item, e o
 * C# não aceita nulo (seria 400).
 */
function achatarImpostoParaRequest(linha: FormGroup, idVendaItem: number): VendaItemImposto | null {
    const campos: Record<string, unknown> = {};
    let algumPreenchido = false;

    for (const [campoServidor, caminho] of Object.entries(MAPA_IMPOSTO)) {
        const valor = linha.get(caminho)?.value;
        const vazio = valor === null || valor === undefined || valor === '';

        campos[campoServidor] = vazio ? null : valor;

        if (!vazio) algumPreenchido = true;
    }

    if (!algumPreenchido) return null;

    // Do servidor: reescritos na gravação, mas o model os declara e o C# não os aceita nulos.
    return {
        idVendaItemImposto: 0,
        idEmpresa: 0,
        idVendaItem,
        datetimeInclusao: new Date().toISOString(),
        ...campos,
    } as unknown as VendaItemImposto;
}
